import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The element id a URL fragment names, or null when it names nothing usable.
 *
 * Split out from the hook below so the rule can be tested without a browser: every vitest file in
 * this project checks logic or source text, none of them render, so this is the part of the
 * behaviour that can be pinned there. The rest — that the element is reached and focused — is a
 * Playwright matter.
 */
export function elementIdFromHash(hash: string): string | null {
  if (!hash || hash === '#') return null;
  let id: string;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    // decodeURIComponent throws on a malformed escape ("#%zz"), which arrives from a hand-edited
    // or truncated URL. A fragment nobody can resolve is not an error worth throwing out of an
    // effect — it is the same nothing as no fragment at all.
    return null;
  }
  return id.trim() === '' ? null : id;
}

/**
 * Send the reader to the element the URL fragment names, once there is one to send them to.
 *
 * The catalogue pages give each row an anchor — `proiect-{id}` on /cercetare, `masura-{id}` on
 * /finantare — so a link elsewhere can point at one row, and the home page's publications strip
 * does exactly that. Those anchors were dead: the rows come from usePublicList, which answers null
 * while the request is in flight, so the browser resolves the fragment against a page whose grid
 * is not in the DOM yet, finds nothing, and never tries again. The reader arrived with the right
 * URL at the top of a forty-card list, with the card they asked for eighteenth and far below the
 * fold. Nothing else in the application recovers a fragment after its data arrives.
 *
 * `loaded` rather than a mount-time effect: the element does not exist until the list does, and
 * that is the whole defect. The fragment comes from useLocation so that following a second link
 * on the same page — same route, new fragment, no remount — moves the reader again.
 *
 * Focus, not only scroll. AppShell says it in the cabinet's own words: `.focus()` is the signal a
 * screen reader acts on, never scrollIntoView alone. A fragment link that moves the viewport and
 * leaves the caret at the top of the document has served the sighted reader and nobody else.
 */
export function useHashTarget(loaded: boolean): void {
  const { hash } = useLocation();

  useEffect(() => {
    if (!loaded) return;
    const id = elementIdFromHash(hash);
    if (!id) return;
    // A fragment that matches no row is ordinary, not exceptional: a link kept from before a
    // project left the catalogue still carries it. The page stays where it is rather than
    // guessing at a nearby row.
    const target = document.getElementById(id);
    if (!target) return;

    // Instant, not smooth. This stands in for the jump the browser would have made itself had the
    // element existed in time, and a page that animates itself after a navigation reads as a
    // glitch — the more so for a reader who has asked for reduced motion.
    target.scrollIntoView({ block: 'start' });
    // preventScroll because the line above has already decided where the page sits; without it,
    // focus() scrolls a second time and can land the row under a sticky header.
    target.focus({ preventScroll: true });
  }, [loaded, hash]);
}
